import React, { useEffect } from "react";
import { Container, Box, Typography, Button, Paper, Stack, Link as MuiLink } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { scrollToAnchorBelowHeader } from "../utils/scrollToAnchor";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MarketingSiteHeader from "./MarketingSiteHeader";
import MarketingSiteFooter from "./MarketingSiteFooter";

/**
 * Public “About us” page: origin story and mission (Canadian / Toronto–based).
 *
 * @returns {React.ReactElement}
 */
const AboutPage = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, "");
    if (hash !== "contact") return undefined;
    const timer = window.setTimeout(() => scrollToAnchorBelowHeader("contact"), 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh" }}>
      <MarketingSiteHeader />

      <Box component="main">
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            background: "linear-gradient(135deg, rgba(74,0,224,0.06), rgba(249,72,74,0.06))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Container maxWidth="md">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              Company
            </Typography>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 1, mb: 3 }}>
              About Quill AI
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, mb: 2 }}>
              Quill AI is a <strong>Canadian</strong> company operating out of <strong>Toronto</strong>. We build tools for
              professionals whose work still lives in documents that demand judgment—scope, nuance, and iteration—not
              one-click boilerplate.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              Our mission is to make <strong>writing documents</strong>, <strong>doing research</strong>, and producing
              client-ready deliverables faster and better, while keeping humans in the loop. We are building a best-in-class
              AI workspace for people who create <strong>dynamic, high-stakes documents</strong> that need real thought,
              input, and collaboration—from proposals and statements of work to research-backed briefs and regulated
              artifacts—so your team can ship quality work with less friction, not less rigor.
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
          <Stack spacing={3}>
            <Typography variant="h5" component="h2" fontWeight={800}>
              What we believe
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              Great documents are not generated in a vacuum—they tie back to meetings, context, and templates your
              organization trusts. Quill AI connects customers, projects, and sessions so drafts stay traceable, editable,
              and aligned with how your team actually works.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              If your role still depends on careful writing and structured research, we are building for you.
            </Typography>
          </Stack>

          <Box
            id="contact"
            component="section"
            sx={{
              mt: 6,
              scrollMarginTop: "88px",
            }}
          >
            <Typography variant="h5" component="h2" fontWeight={800} sx={{ mb: 2 }}>
              Contact
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, mb: 2 }}>
              Questions about Quill AI or partnerships? Reach us at:
            </Typography>
            <Typography variant="body1">
              <MuiLink href="mailto:info@quillai.app" color="secondary" fontWeight={600} underline="hover">
                info@quillai.app
              </MuiLink>
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              mt: 4,
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, rgba(74,0,224,0.95), rgba(249,72,74,0.88))",
              color: "white",
            }}
          >
            <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
              See Quill AI in your workflow
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
              Start free or sign in to explore the workspace.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="contained"
              color="inherit"
              endIcon={<ArrowForwardIcon />}
              sx={{ color: "primary.main", textTransform: "none", fontWeight: 700 }}
            >
              Get started
            </Button>
          </Paper>
        </Container>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default AboutPage;
